import { io } from "socket.io-client"

/**
 * One Socket.IO connection per browser tab.
 *
 * Chat and notifications both want a socket. Two connections means two
 * handshakes, two presence entries for one person, and every broadcast delivered
 * twice. This is a small reference-counted singleton so both features share one.
 *
 * ── The grace period ────────────────────────────────────────────────────────
 *
 * React StrictMode mounts, unmounts and remounts every component in development.
 * Disconnecting the moment the count hits zero means a disconnect/reconnect storm
 * on every render pass — and in production, the same thing happens whenever the
 * last consumer navigates between two screens that both use it. A short delay
 * before actually closing absorbs both.
 *
 * ── Tracked rooms ───────────────────────────────────────────────────────────
 *
 * Room membership lives on the SERVER socket and is destroyed when the connection
 * drops. Callers join from an effect keyed on the room id, so after a reconnect
 * no dependency changed and the join is never re-sent — the tab sits silently
 * outside its own thread room until the user navigates away and back. Tracking
 * the set here lets every `connect` replay it.
 */

let socket = null
let authKey = ""
let refCount = 0
let disconnectTimer = null

const DISCONNECT_GRACE_MS = 1500
const trackedRooms = new Set()

function clearDisconnectTimer() {
  if (disconnectTimer) {
    clearTimeout(disconnectTimer)
    disconnectTimer = null
  }
}

/** Join a room and remember it, so it is rejoined on every reconnect. */
export function joinRoomTracked(room) {
  if (!room || typeof room !== "string") return
  trackedRooms.add(room)
  if (socket?.connected) socket.emit("joinRoom", room)
}

/** Stop tracking a room and leave it. Safe while disconnected. */
export function leaveRoomTracked(room) {
  if (!room || typeof room !== "string") return
  trackedRooms.delete(room)
  if (socket?.connected) socket.emit("leaveRoom", room)
}

export function acquireSharedSocket(url, token, userId) {
  clearDisconnectTimer()
  refCount += 1

  const key = `${token}|${userId}`
  if (socket && authKey === key) return socket

  if (socket) {
    try {
      socket.removeAllListeners()
      socket.disconnect()
    } catch {
      /* already gone */
    }
    socket = null
  }

  // A different identity: the previous user's rooms must not be replayed, and
  // the server would refuse them anyway.
  trackedRooms.clear()
  authKey = key

  socket = io(url, {
    transports: ["websocket"],
    auth: { token },
    reconnection: true,
    reconnectionAttempts: 30,
    reconnectionDelay: 1500,
    reconnectionDelayMax: 15000,
    timeout: 20000,
  })

  const rejoin = () => {
    // The server also joins `user:<id>` from the handshake, so delivery does not
    // depend on this round trip; it is a safety net, and it is how the thread
    // rooms come back.
    socket.emit("joinRoom", `user:${userId}`)
    for (const room of trackedRooms) socket.emit("joinRoom", room)
  }

  socket.on("connect", rejoin)
  if (socket.connected) rejoin()

  return socket
}

export function releaseSharedSocket() {
  refCount = Math.max(0, refCount - 1)
  clearDisconnectTimer()
  disconnectTimer = setTimeout(() => {
    disconnectTimer = null
    if (refCount === 0 && socket) {
      try {
        socket.removeAllListeners()
        socket.disconnect()
      } catch {
        /* already gone */
      }
      socket = null
      authKey = ""
      trackedRooms.clear()
    }
  }, DISCONNECT_GRACE_MS)
}

/** Close immediately — on sign-out, so presence clears without the grace delay. */
export function forceDisconnectSharedSocket() {
  clearDisconnectTimer()
  refCount = 0
  trackedRooms.clear()
  if (socket) {
    try {
      socket.removeAllListeners()
      socket.disconnect()
    } catch {
      /* already gone */
    }
    socket = null
    authKey = ""
  }
}

export function getSharedSocket() {
  return socket
}
